import { stepCountIs } from '@internal/ai-sdk-v5';
import type { ModelMessage, ToolSet } from '@internal/ai-sdk-v5';
import type { MastraPrimitives } from '../../action';
import { MastraBase } from '../../base';
import { MastraError, ErrorDomain, ErrorCategory } from '../../error';
import { loop } from '../../loop';
import type { LoopOptions } from '../../loop/types';
import type { Mastra } from '../../mastra';
import { SpanType, resolveObservabilityContext } from '../../observability';
import { executeWithContextSync } from '../../observability/utils';
import { getToolDefinitionsForTracing } from '../../stream/aisdk/v5/compat/prepare-tools';
import type { MastraModelOutput } from '../../stream/base/output';
import type { ModelManagerModelConfig } from '../../stream/types';
import { delay } from '../../utils';

import type { ModelLoopStreamArgs } from './model.loop.types';
import { resolveResponseModelId } from './server-side-fallback';
import type { MastraModelOptions } from './shared.types';

export class MastraLLMVNext extends MastraBase {
  #models: ModelManagerModelConfig[];
  #mastra?: Mastra;
  #options?: MastraModelOptions;
  #firstModel: ModelManagerModelConfig;

  constructor({
    mastra,
    models,
    options,
  }: {
    mastra?: Mastra;
    models: ModelManagerModelConfig[];
    options?: MastraModelOptions;
  }) {
    super({ name: 'aisdk' });

    this.#options = options;

    if (mastra) {
      this.#mastra = mastra;
      if (mastra.getLogger()) {
        this.__setLogger(this.#mastra.getLogger());
      }
    }

    if (models.length === 0 || !models[0]) {
      const mastraError = new MastraError({
        id: 'LLM_LOOP_MODELS_EMPTY',
        domain: ErrorDomain.LLM,
        category: ErrorCategory.USER,
      });
      this.logger.trackException(mastraError);
      throw mastraError;
    } else {
      this.#models = models;
      this.#firstModel = models[0];
    }
  }

  __registerPrimitives(p: MastraPrimitives) {
    if (p.logger) {
      this.__setLogger(p.logger);
    }
  }

  __registerMastra(p: Mastra) {
    this.#mastra = p;
  }

  getProvider() {
    return this.#firstModel.model.provider;
  }

  getModelId() {
    return this.#firstModel.model.modelId;
  }

  getModel() {
    return this.#firstModel.model;
  }

  getProviderOptions() {
    return this.#firstModel.providerOptions;
  }

  convertToMessages(messages: string | string[] | ModelMessage[]): ModelMessage[] {
    if (Array.isArray(messages)) {
      return messages.map(m => {
        if (typeof m === 'string') {
          return {
            role: 'user',
            content: m,
          };
        }
        return m;
      });
    }

    return [
      {
        role: 'user',
        content: messages,
      },
    ];
  }

  stream<Tools extends ToolSet, OUTPUT = undefined>({
    resumeContext,
    runId,
    stopWhen,
    maxSteps,
    tools = {} as Tools,
    modelSettings,
    toolChoice = 'auto',
    threadId,
    resourceId,
    structuredOutput,
    options,
    inputProcessors,
    llmRequestInputProcessors,
    outputProcessors,
    errorProcessors,
    hasConfiguredErrorProcessors,
    returnScorerData,
    providerOptions,
    messageList,
    requireToolApproval,
    toolCallConcurrency,
    eagerToolExecution,
    _internal,
    agentId,
    agentVersionId,
    agentName,
    toolCallId,
    requestContext,
    actor,
    mcp,
    methodType,
    includeRawChunks,
    experimentalTransform,
    hideSignals,
    autoResumeSuspendedTools,
    maxProcessorRetries,
    processorStates,
    activeTools,
    isTaskComplete,
    goal,
    onIterationComplete,
    workspace,
    ...rest
  }: ModelLoopStreamArgs<Tools, OUTPUT>): MastraModelOutput<OUTPUT> {
    const observabilityContext = resolveObservabilityContext(rest);
    // `maxSteps` is sugar for the stop condition `stepCountIs(maxSteps)`. When a
    // custom `stopWhen` is also provided, compose the two (the loop ORs stop
    // conditions) instead of letting the maxSteps cap replace the user's
    // condition. The default cap only applies when neither is set.
    let stopWhenToUse;
    if (maxSteps && typeof maxSteps === 'number') {
      const userConditions = stopWhen ? (Array.isArray(stopWhen) ? stopWhen : [stopWhen]) : [];
      stopWhenToUse = [stepCountIs(maxSteps), ...userConditions];
    } else {
      stopWhenToUse = stopWhen ?? stepCountIs(5);
    }

    const messages = messageList.get.all.aiV5.model();

    const firstModel = this.#firstModel.model;

    const parentSpan = observabilityContext.tracingContext.currentSpan;
    // Serialized once per generation so exporters can surface the tool schemas
    // the model received; skipped entirely when tracing is off.
    const toolDefinitions = parentSpan
      ? getToolDefinitionsForTracing({ tools, toolChoice, activeTools: activeTools as string[] | undefined })
      : undefined;

    const modelSpan = parentSpan?.createChildSpan({
      name: `llm: '${firstModel.modelId}'`,
      type: SpanType.MODEL_GENERATION,
      input: {
        messages: [...messageList.getAllSystemMessages(), ...messages],
      },
      attributes: {
        model: firstModel.modelId,
        provider: firstModel.provider,
        streaming: true,
        parameters: modelSettings,
        ...(toolDefinitions ? { tools: toolDefinitions } : {}),
      },
      metadata: {
        runId,
        threadId,
        resourceId,
      },
      tracingPolicy: this.#options?.tracingPolicy,
      requestContext,
    });

    if (modelSpan) {
      executeWithContextSync({
        span: modelSpan,
        fn: () =>
          this.logger.debug('Streaming text', {
            runId,
            threadId,
            resourceId,
            messages,
            tools: Object.keys(tools || {}),
          }),
      });
    }

    // Create model span tracker that will be shared across all LLM execution steps.
    // The agentic loop calls setInferenceContext + startInference per-step so the
    // MODEL_INFERENCE span reflects the post-processor tool set / parameters.
    const modelSpanTracker = modelSpan?.createTracker();

    try {
      const loopOptions: LoopOptions<Tools, OUTPUT> = {
        mastra: this.#mastra,
        resumeContext,
        runId,
        toolCallId,
        messageList,
        models: this.#models,
        logger: this.logger,
        tools,
        stopWhen: stopWhenToUse,
        toolChoice,
        modelSettings,
        providerOptions,
        _internal,
        structuredOutput,
        inputProcessors,
        llmRequestInputProcessors,
        outputProcessors,
        errorProcessors,
        hasConfiguredErrorProcessors,
        returnScorerData,
        modelSpanTracker,
        requireToolApproval,
        toolCallConcurrency,
        eagerToolExecution,
        agentId,
        agentVersionId,
        agentName,
        requestContext,
        actor,
        mcp,
        methodType,
        includeRawChunks,
        experimentalTransform,
        hideSignals,
        autoResumeSuspendedTools,
        maxProcessorRetries,
        processorStates,
        activeTools,
        isTaskComplete,
        goal,
        onIterationComplete,
        workspace,
        ...observabilityContext,
        options: {
          ...options,
          onStepFinish: async props => {
            try {
              await options?.onStepFinish?.({ ...props, runId: runId! });
            } catch (e: unknown) {
              const mastraError = new MastraError(
                {
                  id: 'LLM_STREAM_ON_STEP_FINISH_CALLBACK_EXECUTION_FAILED',
                  domain: ErrorDomain.LLM,
                  category: ErrorCategory.USER,
                  details: {
                    modelId: props.model?.modelId as string,
                    modelProvider: props.model?.provider as string,
                    runId: runId ?? 'unknown',
                    threadId: threadId ?? 'unknown',
                    resourceId: resourceId ?? 'unknown',
                    finishReason: props?.finishReason as string,
                    toolCalls: props?.toolCalls ? JSON.stringify(props.toolCalls) : '',
                    toolResults: props?.toolResults ? JSON.stringify(props.toolResults) : '',
                    usage: props?.usage ? JSON.stringify(props.usage) : '',
                  },
                },
                e,
              );
              modelSpanTracker?.reportGenerationError({ error: mastraError });
              this.logger.trackException(mastraError);
              throw mastraError;
            }

            this.logger.debug('Stream step change', {
              text: props?.text,
              toolCalls: props?.toolCalls,
              toolResults: props?.toolResults,
              finishReason: props?.finishReason,
              usage: props?.usage,
              runId,
            });

            const remainingTokens = parseInt(props?.response?.headers?.['x-ratelimit-remaining-tokens'] ?? '', 10);
            if (!isNaN(remainingTokens) && remainingTokens > 0 && remainingTokens < 2000) {
              this.logger.warn('Rate limit approaching, waiting 10 seconds', { runId, remainingTokens });
              const rateLimitSpan = modelSpan?.createChildSpan({
                name: 'rate-limit-sleep',
                type: SpanType.GENERIC,
                metadata: { remainingTokens, delayMs: 10_000 },
              });
              await delay(10 * 1000, options?.abortSignal);
              rateLimitSpan?.end();
            }
          },

          onFinish: async (props, context) => {
            // End the model generation span BEFORE calling the user's onFinish callback
            // This ensures the model span ends before the agent span
            // Pass raw usage and providerMetadata - ModelSpanTracker will convert to UsageStats
            modelSpanTracker?.endGeneration({
              output: {
                files: props?.files,
                object: props?.object,
                reasoning: props?.reasoning,
                reasoningText: props?.reasoningText,
                sources: props?.sources,
                text: props?.text,
                warnings: props?.warnings,
                // Flatten tool-call chunks so exporters (e.g. PostHog) see the same
                // { toolCallId, toolName, args } shape as the non-loop path.
                toolCalls: props?.toolCalls?.length
                  ? props.toolCalls.map(tc => ({
                      toolCallId: tc.payload.toolCallId,
                      toolName: tc.payload.toolName,
                      args: tc.payload.args,
                    }))
                  : undefined,
              },
              attributes: {
                finishReason: props?.finishReason,
                responseId: props?.response.id,
                // Account for Anthropic server-side fallbacks: when the primary
                // model declines a turn and a fallback serves it, attribute the
                // response to the model that actually generated it.
                responseModel: resolveResponseModelId(props?.providerMetadata, props?.response.modelId),
              },
              usage: props?.totalUsage,
              providerMetadata: props?.providerMetadata,
              stepProviderMetadata: props?.steps.map(step => step.providerMetadata),
            });

            try {
              await options?.onFinish?.({ ...props, runId: runId! }, context);
            } catch (e: unknown) {
              const mastraError = new MastraError(
                {
                  id: 'LLM_STREAM_ON_FINISH_CALLBACK_EXECUTION_FAILED',
                  domain: ErrorDomain.LLM,
                  category: ErrorCategory.USER,
                  details: {
                    modelId: props.model?.modelId as string,
                    modelProvider: props.model?.provider as string,
                    runId: runId ?? 'unknown',
                    threadId: threadId ?? 'unknown',
                    resourceId: resourceId ?? 'unknown',
                    finishReason: props?.finishReason as string,
                    toolCalls: props?.toolCalls ? JSON.stringify(props.toolCalls) : '',
                    toolResults: props?.toolResults ? JSON.stringify(props.toolResults) : '',
                    usage: props?.usage ? JSON.stringify(props.usage) : '',
                  },
                },
                e,
              );
              modelSpanTracker?.reportGenerationError({ error: mastraError });
              this.logger.trackException(mastraError);
              throw mastraError;
            }

            this.logger.debug('Stream finished', {
              text: props?.text,
              toolCalls: props?.toolCalls,
              toolResults: props?.toolResults,
              finishReason: props?.finishReason,
              usage: props?.usage,
              runId,
              threadId,
              resourceId,
            });
          },
        },
        maxSteps,
      };

      return loop(loopOptions);
    } catch (e: unknown) {
      const mastraError = new MastraError(
        {
          id: 'LLM_STREAM_TEXT_AI_SDK_EXECUTION_FAILED',
          domain: ErrorDomain.LLM,
          category: ErrorCategory.THIRD_PARTY,
          details: {
            modelId: firstModel.modelId,
            modelProvider: firstModel.provider,
            runId: runId ?? 'unknown',
            threadId: threadId ?? 'unknown',
            resourceId: resourceId ?? 'unknown',
          },
        },
        e,
      );
      modelSpanTracker?.reportGenerationError({ error: mastraError });
      throw mastraError;
    }
  }
}
